import { zodResolver } from '@hookform/resolvers/zod';
import _isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useToasts } from 'react-toast-notifications';
import Input from 'src/components/CustomInput';
import EditedByInfo from 'src/components/EditedByInfo';
import Loader from 'src/components/loader';
import TruncatedTitle from 'src/components/TruncatedTitle';
import FeaturesContext from 'src/context/FeaturesContext';
import {
  useAutoDocumentParsingJobs,
  useDocumentParserInstruction,
  useDocumentParserInstructionReports,
} from 'src/hooks/services/useDocumentParser';
import { useUsersByUri } from 'src/hooks/services/useUser';
import useActionPanelStore from 'src/stores/useActionPanelStore';
import { api } from 'src/utils/api';
import { API_RESOURCES, COMMON_BACKGROUND_JOB_STATUSES } from 'src/utils/constants';
import { FEATURES, isFeatureEnabled } from 'src/utils/features';
import { getUuid } from 'src/utils/url';
import { createDocumentParserFieldSchema } from 'src/utils/validation';
import { z } from 'zod';

const DocumentParserTemplateEditModal = ({
  parserTemplateUri,
  documentUri,
  workInstructionReportUri,
}) => {
  const [jobsRefetchInterval, setJobsRefetchInterval] = useState(false);
  const { data: templateFields = [], isInitialLoading } =
    useDocumentParserInstruction(parserTemplateUri);
  const {
    data: existingReports = [],
    isInitialLoading: reportsLoading,
    refetch: refetchReports,
  } = useDocumentParserInstructionReports(documentUri, workInstructionReportUri);

  const usersUris = existingReports.map(report => report.user).filter(Boolean);

  const { data: usersByUri = {}, isInitialLoading: usersByUriLoading } = useUsersByUri(usersUris);

  const { closeActionPanel, setActionPanelProps } = useActionPanelStore();
  const { addToast } = useToasts();

  const { features } = useContext(FeaturesContext);
  const isAutoDocumentParsingFeatureEnabled = isFeatureEnabled(
    features,
    FEATURES.AUTO_DOCUMENT_PARSING
  );
  const { data: existingJobs = [], isInitialLoading: autoDocumentParsingLoading } =
    useAutoDocumentParsingJobs(
      documentUri,
      jobsRefetchInterval,
      isAutoDocumentParsingFeatureEnabled
    );

  const resourcesLoading =
    usersByUriLoading || isInitialLoading || reportsLoading || autoDocumentParsingLoading;

  const validationSchema = useMemo(() => {
    if (!templateFields.length) return z.object({});

    // Use field indexes instead of URLs to avoid nested object parsing issues
    const schemaShape = Object.fromEntries(
      templateFields.map((_, index) => [index, createDocumentParserFieldSchema()])
    );

    // Add refinement to ensure at least one field has a value (only for new entries)
    return z.object(schemaShape).refine(
      data => {
        // If we're editing existing data, allow clearing all fields
        if (existingReports.length > 0) return true;

        // For new entries, check if at least one field has a non-empty value
        return Object.values(data).some(field => field.value !== '' && field.value !== null);
      },
      {
        message: 'At least one field must have a value',
      }
    );
  }, [templateFields, existingReports]);

  const defaultValues = useMemo(() => {
    if (!templateFields.length) return {};

    // Lookup map for the existing fields
    const existingReportsMap = Object.fromEntries(
      existingReports.map(report => [
        report.parser_instruction,
        {
          value: report.value ?? '',
          unit: report.unit ?? '',
          reportUri: report.uri, // Store the report URI for PUT requests
          reportData: report, // Store full report data for EditedByInfo component
        },
      ])
    );

    // Map template fields to form data with existing values
    const formDefaults = Object.fromEntries(
      templateFields.map((field, index) => {
        const existingData = existingReportsMap[field.uri] ?? {
          value: '',
          unit: '',
          reportUri: null,
          reportData: null,
        };
        return [index, existingData];
      })
    );

    return formDefaults;
  }, [templateFields, existingReports]);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting, isDirty, dirtyFields },
  } = useForm({
    resolver: zodResolver(validationSchema),
    values: defaultValues,
  });

  const processingJob = useMemo(
    () =>
      existingJobs.some(job =>
        [
          COMMON_BACKGROUND_JOB_STATUSES.PROCESSING,
          COMMON_BACKGROUND_JOB_STATUSES.PENDING,
        ].includes(job.status)
      ),
    [existingJobs]
  );

  useEffect(() => {
    if (processingJob) {
      setJobsRefetchInterval(2000);
    } else if (!existingReports.length) {
      setJobsRefetchInterval(false);
      refetchReports();
    }
  }, [
    refetchReports,
    processingJob,
    setActionPanelProps,
    existingReports.length,
    setJobsRefetchInterval,
  ]);

  // Watch all form values to check if at least one field has a value
  const watchedValues = watch();
  const hasAtLeastOneValue = useMemo(() => {
    if (!watchedValues) return false;
    return Object.values(watchedValues).some(field => field?.value !== '' && field?.value != null);
  }, [watchedValues]);

  // Determine if we're editing existing data or creating new
  const isEditing = existingReports.length > 0;
  const buttonText = isEditing ? 'Update' : 'Save';

  // Helpers for showing compact "Current" chips per sub-field (value/unit)
  const getInitialFieldState = index => defaultValues?.[index] ?? { value: '', unit: '' };
  const getCurrentFieldState = index => watchedValues?.[index] ?? { value: '', unit: '' };

  const shouldShowCurrentForValue = index => {
    if (!dirtyFields?.[index]?.value) return false;
    const initial = getInitialFieldState(index).value ?? '';
    if (initial === '') return false; // Only show when backend has a value
    const current = getCurrentFieldState(index).value ?? '';
    return current !== initial;
  };

  const shouldShowCurrentForUnit = index => {
    if (!dirtyFields?.[index]?.unit) return false;
    const initial = getInitialFieldState(index).unit ?? '';
    if (initial === '') return false; // Only show when backend has a unit
    const current = getCurrentFieldState(index).unit ?? '';
    return current !== initial;
  };

  // Helper function to create API request based on whether report exists
  const createApiRequest = (field, formFieldData) => {
    const { value, unit, reportUri } = formFieldData;

    const requestData = {
      json: {
        /* eslint-disable camelcase */
        parser_instruction: field.uri,
        document: documentUri,
        value: value === '' || value == null ? '' : value.toString(),
        unit: unit ? unit.trim() : '',
      },
    };

    // If reportUri exists, this is an update (PUT), otherwise it's a create (POST)
    if (reportUri) {
      const reportId = getUuid(reportUri);
      return api.put(
        `${API_RESOURCES.DOCUMENT_PARSER_INSTRUCTION_REPORT}/${reportId}/`,
        requestData
      );
    } else {
      requestData.json.work_instruction_report = workInstructionReportUri;
      // Create new report with POST
      return api.post(`${API_RESOURCES.DOCUMENT_PARSER_INSTRUCTION_REPORT}/`, requestData);
    }
  };

  const onSubmit = async formData => {
    try {
      setActionPanelProps({ isSubmitting: true });

      // Get indexes of fields that have been modified
      const changedFieldIndexes = Object.keys(dirtyFields).map(Number);

      // For existing reports (updates), we need to submit even if value is empty (to clear it)
      // For new reports, only submit if there's a value
      const fieldsToSubmit = changedFieldIndexes.filter(index => {
        const fieldData = formData[index];
        const hasValue = fieldData.value !== '' && fieldData.value != null;
        const isExistingReport = fieldData.reportUri != null;
        const hasOnlyUnit =
          !isExistingReport && fieldData.unit && fieldData.unit.length > 0 && !hasValue;

        // Don't submit new fields with only unit
        if (hasOnlyUnit) return false;

        // Submit if: has a value OR it's an existing report being cleared
        return hasValue || isExistingReport;
      });

      const apiRequests = fieldsToSubmit.map(index => {
        const field = templateFields[index];
        const data = formData[index];
        return createApiRequest(field, data);
      });

      // Execute API requests for changed fields
      const results = await Promise.allSettled(apiRequests);

      // Check for failures
      const failures = results.filter(result => result.status === 'rejected');
      if (failures.length > 0) {
        const errorMessages = failures
          .map(result => result.reason?.message)
          .filter(Boolean)
          .join(', ');
        throw new Error(errorMessages || 'Some submissions failed');
      }

      addToast('Fields updated successfully', { appearance: 'success' });
      closeActionPanel();
    } catch (error) {
      console.error('Error submitting parser template data:', error);
      addToast(`Error submitting data: ${error.message ?? 'Please try again'}`, {
        appearance: 'error',
      });
    } finally {
      setActionPanelProps({ isSubmitting: false });
    }
  };

  if (resourcesLoading || processingJob) {
    return (
      <div className='text-center py-4'>
        <Loader showText text='template data...' />
      </div>
    );
  }

  if (_isEmpty(templateFields)) {
    return <div className='text-center py-4'>Template fields not found.</div>;
  }

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)}>
      <div>
        {templateFields.map((field, index) => {
          const fieldErrors = errors[index];

          return (
            <div key={field.uri} className={index > 0 ? 'mt-3' : ''}>
              <div className='mb-2'>
                <div className='d-flex justify-content-between align-items-start flex-wrap'>
                  <h6 className='mb-0 text-dark font-weight-bold d-flex align-items-start flex-shrink-1'>
                    <span className='mr-2'>{index + 1}.</span>
                    <TruncatedTitle
                      title={field.name}
                      titleClassName='mb0'
                      maxTextLength={45}
                      maxTextLengthMobile={35}
                    />
                  </h6>
                  <div className='flex-shrink-0'>
                    <EditedByInfo
                      report={defaultValues[index]?.reportData}
                      usersByUri={usersByUri}
                    />
                  </div>
                </div>
              </div>
              <div className='row no-gutters'>
                <div className='col-7 pr-2'>
                  <Controller
                    name={`${index}.value`}
                    control={control}
                    render={({ field: controllerField }) => (
                      <Input
                        label='Value'
                        type='text'
                        value={controllerField.value}
                        placeholder='e.g., 150'
                        isError={!!fieldErrors?.value}
                        errorMessage={fieldErrors?.value?.message}
                        style={{ marginBottom: 0 }}
                        onChange={controllerField.onChange}
                      />
                    )}
                  />
                  {shouldShowCurrentForValue(index) && (
                    <div className='mt-1'>
                      <span className='text-muted dp-current-badge'>
                        <strong className='dp-current-label'>Current</strong>
                        <span className='dp-current-text'>{getInitialFieldState(index).value}</span>
                      </span>
                    </div>
                  )}
                </div>

                <div className='col-5 pl-1'>
                  <Controller
                    name={`${index}.unit`}
                    control={control}
                    render={({ field: controllerField }) => (
                      <Input
                        label='Unit'
                        type='text'
                        value={controllerField.value}
                        placeholder='e.g., kg'
                        isError={!!fieldErrors?.unit}
                        errorMessage={fieldErrors?.unit?.message}
                        style={{ marginBottom: 0 }}
                        onChange={controllerField.onChange}
                      />
                    )}
                  />
                  {shouldShowCurrentForUnit(index) && (
                    <div className='mt-1'>
                      <span className='text-muted dp-current-badge'>
                        <strong className='dp-current-label'>Current</strong>
                        <span className='dp-current-text'>{getInitialFieldState(index).unit}</span>
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {index < templateFields.length - 1 && (
                <hr className='my-3' style={{ borderColor: '#e9ecef', opacity: 0.5 }} />
              )}
            </div>
          );
        })}
      </div>

      <div className='text-center'>
        <button
          type='submit'
          className='btn btn-lg btn-primary action-btn btn-block mt30'
          disabled={isSubmitting || (!isEditing && !hasAtLeastOneValue) || (isEditing && !isDirty)}
        >
          {buttonText}
        </button>

        <button
          type='button'
          className='btn btn-lg btn-block'
          disabled={isSubmitting}
          onClick={closeActionPanel}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

DocumentParserTemplateEditModal.propTypes = {
  parserTemplateUri: PropTypes.string.isRequired,
  documentUri: PropTypes.string.isRequired,
  workInstructionReportUri: PropTypes.string.isRequired,
};

export default DocumentParserTemplateEditModal;
